
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import Badge from '../../components/base/Badge';
import Button from '../../components/base/Button';
import { mockCalendarEvents, mockDashboardData } from '../../mocks/activities';

export default function MarketingCalendar() {
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day'>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  const formatCurrency = (amount: number) => {
    return `¥${(amount / 10000).toFixed(1)}万`;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-500',
      planning: 'bg-yellow-500',
      completed: 'bg-gray-500',
      draft: 'bg-blue-500'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500';
  };

  const getTypeColor = (type: string) => {
    const colors = {
      promotion: 'bg-red-100 text-red-800 border-red-200',
      launch: 'bg-blue-100 text-blue-800 border-blue-200',
      member: 'bg-purple-100 text-purple-800 border-purple-200',
      seasonal: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDay = (day: number) => {
    if (!day) return [];
    const dayStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return mockCalendarEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const checkDate = new Date(dayStr);
      return checkDate >= eventStart && checkDate <= eventEnd;
    });
  };

  const selectedEventData = selectedEvent 
    ? mockCalendarEvents.find(e => e.id === selectedEvent)
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
        {/* 页面标题和控制 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">营销日历</h1>
            <p className="text-gray-600">可视化管理营销活动时间线</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* 视图切换 */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { key: 'month', label: '月', icon: 'ri-calendar-2-line' },
                { key: 'week', label: '周', icon: 'ri-calendar-week-line' },
                { key: 'day', label: '日', icon: 'ri-calendar-line' }
              ].map((view) => (
                <button
                  key={view.key}
                  onClick={() => setCurrentView(view.key as any)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    currentView === view.key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className={`${view.icon} mr-1`}></i>
                  {view.label}视图
                </button>
              ))}
            </div>
            
            {/* 日期导航 */}
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
              >
                <i className="ri-arrow-left-line"></i>
              </Button>
              <span className="font-semibold text-gray-900 min-w-[120px] text-center">
                {currentDate.getFullYear()}年{currentDate.getMonth() + 1}月
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
              >
                <i className="ri-arrow-right-line"></i>
              </Button>
            </div>
            
            <Button>
              <i className="ri-add-line mr-2"></i>
              添加活动
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 日历主体 */}
          <div className="lg:col-span-3">
            <Card padding="sm">
              {/* 日历头部 */}
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-t-lg overflow-hidden">
                {['日', '一', '二', '三', '四', '五', '六'].map((day) => (
                  <div key={day} className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* 日历主体 */}
              <div className="grid grid-cols-7 gap-px bg-gray-200">
                {generateCalendarDays().map((day, index) => {
                  const events = day ? getEventsForDay(day) : [];
                  const isToday = day && 
                    new Date().getDate() === day && 
                    new Date().getMonth() === currentDate.getMonth() && 
                    new Date().getFullYear() === currentDate.getFullYear();
                  
                  return (
                    <div 
                      key={index} 
                      className={`bg-white min-h-[120px] p-2 ${day ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    >
                      {day && (
                        <>
                          <div className={`text-sm font-medium mb-2 ${
                            isToday ? 'text-blue-600' : 'text-gray-900'
                          }`}>
                            {isToday && <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">{day}</div>}
                            {!isToday && day}
                          </div>
                          <div className="space-y-1">
                            {events.slice(0, 2).map((event) => (
                              <div
                                key={event.id}
                                onClick={() => setSelectedEvent(event.id)}
                                className={`px-2 py-1 rounded text-xs font-medium cursor-pointer border ${getTypeColor(event.type)}`}
                              >
                                <div className="flex items-center space-x-1">
                                  <div className={`w-2 h-2 rounded-full ${getStatusColor(event.status)}`}></div>
                                  <span className="truncate">{event.title}</span>
                                </div>
                              </div>
                            ))}
                            {events.length > 2 && (
                              <div className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                +{events.length - 2} 更多
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* 侧边栏 */}
          <div className="space-y-6">
            {/* 多维度看板 */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">本月概览</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">活动总数</span>
                  <span className="font-semibold text-gray-900">{mockCalendarEvents.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">进行中</span>
                  <span className="font-semibold text-green-600">
                    {mockCalendarEvents.filter(e => e.status === 'active').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">总预算</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(mockCalendarEvents.reduce((sum, e) => sum + e.budget, 0))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">已消耗</span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(mockCalendarEvents.reduce((sum, e) => sum + e.spent, 0))}
                  </span>
                </div>
              </div>
            </Card>

            {/* 风险预警 */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">风险预警</h3>
              <div className="space-y-3">
                {mockDashboardData.riskAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${alert.severity === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                      <p className="text-xs text-gray-600 mt-1">2小时前</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* 活动详情 */}
            {selectedEventData && (
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">活动详情</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">{selectedEventData.title}</h4>
                    <div className="flex items-center space-x-2 mb-2">
                      <Badge 
                        variant={selectedEventData.status === 'active' ? 'success' : 
                                selectedEventData.status === 'planning' ? 'warning' : 'gray'}
                      >
                        {selectedEventData.status === 'active' ? '进行中' : 
                         selectedEventData.status === 'planning' ? '筹备中' : '已完成'}
                      </Badge>
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(selectedEventData.status)}`}></div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {selectedEventData.start} - {selectedEventData.end}
                    </p>
                  </div>
                  
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">进度</span>
                      <span className="font-medium">{selectedEventData.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${selectedEventData.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">预算</span>
                      <span className="font-medium">{formatCurrency(selectedEventData.budget)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">已用</span>
                      <span className="font-medium text-red-600">{formatCurrency(selectedEventData.spent)}</span>
                    </div>
                  </div>
                  
                  <div className="pt-3">
                    <Button variant="outline" size="sm" className="w-full">
                      查看详情
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* 快速操作 */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <i className="ri-download-line mr-2"></i>
                  导出日历
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <i className="ri-file-chart-line mr-2"></i>
                  生成报告
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <i className="ri-settings-line mr-2"></i>
                  预算调整
                </Button>
              </div>
            </Card>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
